import Typography from "@mui/material/Typography";
import { NotificationChannel } from "@sinch/components/application/Messages/useMessageChannels";
import { AutocompleteInput } from "@sinch/components/form/AutocompleteInput/AutocompleteInput";
import { UserRoleEnum } from "@sinch/core/entities/serverEnums";
import { t } from "i18next";
import { isNil, pick } from "ramda";
import React, { ReactNode, useCallback, useMemo } from "react";

import { RecipientsOptions, RecipientTag } from "./RecipientTag";
import { useGroupedRecipients } from "./useGroupedRecipients";
import { RECIPIENTS_OPTIONS_LIMIT, useRecipients } from "./useRecipients";
import { AutocompleteInputProps } from "@sinch/components/form/AutocompleteInput/types";
import { Link, Stack } from "@mui/material";
import { RecipientFragment } from "@sinch/components/application/Messages/MessageForm/queries.types";

export interface RecipientsInputProps extends Omit<AutocompleteInputProps<RecipientsOptions, true>, "ref" | "options"> {
  channels: NotificationChannel[] | null;
  readOnly?: boolean;
  channelAddressProp: "phone" | "email";
  disabled?: boolean;
  showRole?: boolean;
  roles?: UserRoleEnum[];
  /**
   * When true, user groups are fetched and merged into the recipients value.
   * Group options are hidden from the dropdown but still rendered as tags.
   */
  useGroups?: boolean;
  /**
   * Optional extra content rendered inside a recipient tag, e.g. a warning
   * that the recipient does not fit the chosen audience on every position.
   */
  renderTagHint?: (value: string) => ReactNode;
  /**
   * Optional content shown right under the label, above the input,
   * e.g. the job audience chips.
   */
  filters?: ReactNode;
}

export const RecipientsInput = ({
  channels,
  readOnly,
  channelAddressProp,
  disabled,
  showRole,
  roles,
  useGroups,
  renderTagHint,
  filters,
  ...props
}: RecipientsInputProps) => {
  const { recipientsField, recipients, value, groupOptions, handleChange, isGroup, filterOptions } =
    useGroupedRecipients({ disabled, useGroups });

  const { isSomeInvalid, loading, onSearch, options, valueOptions, initLoading, recipientsData } = useRecipients({
    value: recipients,
    channels: channels ?? [],
    channelAddressProp,
    showRole,
    roles,
  });

  // Merge group options into the dropdown so already-selected groups resolve to
  // a tag label, without duplicating values already present in recipient options.
  const mergedOptions = useMemo<RecipientsOptions[]>(
    () => [...options.map(pick(["value", "label", "option"])), ...groupOptions],
    [options, groupOptions]
  );

  // Label lookup for the selected tags. The dropdown (`mergedOptions`) only holds
  // current search results, so on first load a selected recipient is resolved from
  // `valueOptions` (fetched by id) rather than the raw id the autocomplete falls
  // back to. `groupOptions` cover selected user groups.
  const labelByValue = useMemo(
    () => new Map([...options, ...valueOptions, ...groupOptions].map(({ value, label }) => [value, label])),
    [options, valueOptions, groupOptions]
  );

  const handleRemoveUnavailable = useCallback(() => {
    handleChange(
      value?.filter((val) => {
        // Read availability from the resolved selected recipients, not the search
        // `options` - the dropdown only holds current search results, which may not
        // contain every selected recipient.
        const availableChannels: string[] | null =
          recipientsData.find((item) => item.id === val)?.availableNotificationChannels ?? null;

        return isNil(availableChannels) || (channels?.some((channel) => availableChannels.includes(channel)) ?? false);
      })
    );
  }, [value, recipientsData, channels, handleChange]);

  return (
    <AutocompleteInput<RecipientsOptions, true>
      {...recipientsField}
      {...props}
      // the helper text holds block content (Stack, Typography), so it cannot stay a <p>
      FormHelperTextProps={{ component: "div", ...props.FormHelperTextProps }}
      belowLabel={filters}
      disableCloseOnSelect
      filterOptions={filterOptions}
      getOptionDisabled={({ value }) => {
        const { availableChannels } = options.find((item) => value === item.value) ?? {
          availableChannels: null,
          roleStatus: null,
        };

        return !(
          isNil(availableChannels) ||
          (channels?.some((channel) => availableChannels?.includes(channel)) ?? false)
        );
      }}
      getOptionLabel={(option) => option.label}
      helperText={
        <Stack>
          {isSomeInvalid ? (
            <Stack direction="row" gap={0.5}>
              <Typography color="warning">{t("MessageDialog.someRecipientsHasNotChannel")}</Typography>
              <Link onClick={handleRemoveUnavailable}>{t("MessageDialog.removeUnavailable")}</Link>
            </Stack>
          ) : undefined}
          {props.helperText}
        </Stack>
      }
      label={t("Messages.recipientsInputLabel")}
      limitCount={RECIPIENTS_OPTIONS_LIMIT}
      limitTags={12}
      loading={loading}
      multiple
      onChange={handleChange}
      onSearch={onSearch}
      openOnFocus
      options={mergedOptions}
      readOnly={readOnly}
      renderTags={(vals, getTagProps) =>
        vals.map(({ value: tagValue, label }, index) => {
          const { key, ...tagProps } = getTagProps({ index });
          return (
            <RecipientTag
              key={key}
              channels={channels}
              hint={renderTagHint?.(String(tagValue))}
              isGroup={isGroup(tagValue)}
              label={labelByValue.get(tagValue) ?? label}
              recipientData={recipientsData as RecipientFragment[]}
              tagProps={tagProps}
              value={tagValue}
            />
          );
        })
      }
      skeleton={initLoading}
      value={value}
    />
  );
};
